import Select from '../ui/Select'
import { useState } from 'react'

import type { ColumnInfo } from '../api/client'
import { colorOf, GROUP_COLOR_NAMES } from './palette'

/** 完成度的表达方式（M9.2）：不设 / 是否完成 / 百分比 —— 后两者二选一 */
type DoneMode = 'none' | 'bool' | 'percent'

interface Row {
  /** 原来的标签索引；新增的标签为 null。用于生成值重映射计划 */
  from: number | null
  text: string
  color: string
  done: DoneMode
  /** 百分比模式下的完成度（0–100） */
  pct: number
}

interface LabelSettings {
  color_mapping?: Record<string, string>
  label_progress?: Record<string, number>
  label_progress_mode?: Record<string, DoneMode>
  done_colors?: number[] | string[]
  [k: string]: unknown
}

function parseRows(column: ColumnInfo): Row[] {
  const labels = (column.labels as { labels?: Record<string, string> } | null)?.labels ?? {}
  const settings = (column.settings as LabelSettings | null) ?? {}
  const colors = settings.color_mapping ?? {}
  const progress = settings.label_progress ?? {}
  const modes = settings.label_progress_mode ?? {}
  const doneColors = new Set((settings.done_colors ?? []).map((x) => String(x)))
  const keys = Object.keys(labels).sort((a, b) => Number(a) - Number(b))
  return keys.map((k) => {
    // 兼容旧数据：只有 done_colors 时按「是否完成」处理
    const pct = typeof progress[k] === 'number' ? progress[k] : doneColors.has(k) ? 100 : 0
    const mode: DoneMode =
      modes[k] === 'bool' || modes[k] === 'percent'
        ? modes[k]
        : doneColors.has(k) || pct > 0
          ? doneColors.has(k)
            ? 'bool'
            : 'percent'
          : 'none'
    return { from: Number(k), text: labels[k] ?? '', color: colors[k] ?? '#c4c4c4', done: mode, pct }
  })
}

export interface LabelsEditorProps {
  column: ColumnInfo
  busy: boolean
  onCancel: () => void
  /** 交出序列化后的 labels、settings 与值重映射计划（都是 JSON 文本） */
  onSave: (
    labelsJson: string,
    settingsJson: string,
    plan: { remap: Record<string, string>; removed: string[] },
  ) => void
}

/**
 * 列设置：编辑标签表（M7）+ 每个标签的完成度（M9.2）。
 *
 * 处理三部分：
 * - `labels.labels{idx:text}`
 * - `settings.color_mapping{idx:hex}`
 * - `settings.label_progress{idx:0..100}` + `settings.label_progress_mode{idx:"bool"|"percent"}`
 *
 * 索引即列值里存的东西，所以**顺序必须稳定**：保存时按当前行序重编号，
 * `label_progress` / `done_colors` 与标签一起重映射（都在同一份 plan 里）。
 *
 * 完成度规则（用户定）：百分比上限 100；「是否完成」与「百分比」二选一。
 */
export default function LabelsEditor({ column, busy, onCancel, onSave }: LabelsEditorProps) {
  const [rows, setRows] = useState<Row[]>(() => parseRows(column))
  const [pickFor, setPickFor] = useState<number | null>(null)
  // M9.2-fix4：色板改 fixed（绝对定位会把可滚动区撑高、逼出竖向滚动条）
  const [pickPos, setPickPos] = useState<{ x: number; y: number } | null>(null)

  const patch = (i: number, next: Partial<Row>) =>
    setRows((prev) => prev.map((x, j) => (j === i ? { ...x, ...next } : x)))
  const setText = (i: number, text: string) => patch(i, { text })
  const setColor = (i: number, color: string) => {
    patch(i, { color })
    setPickFor(null)
  }
  const setDone = (i: number, mode: DoneMode) =>
    patch(i, { done: mode, pct: mode === 'bool' ? 100 : mode === 'none' ? 0 : rows[i]?.pct ?? 0 })
  const setPct = (i: number, raw: string) => {
    const n = Math.max(0, Math.min(100, Math.round(Number(raw) || 0)))
    patch(i, { pct: n })
  }

  const save = () => {
    const labels: Record<string, string> = {}
    const colors: Record<string, string> = {}
    const progress: Record<string, number> = {}
    const modes: Record<string, DoneMode> = {}
    const doneColors: number[] = []
    const remap: Record<string, string> = {}

    rows.forEach((r, i) => {
      labels[String(i)] = r.text
      colors[String(i)] = r.color
      if (r.done === 'bool') {
        progress[String(i)] = 100
        modes[String(i)] = 'bool'
        doneColors.push(i)
      } else if (r.done === 'percent') {
        progress[String(i)] = r.pct
        modes[String(i)] = 'percent'
        if (r.pct >= 100) doneColors.push(i)
      }
      if (r.from !== null) remap[String(r.from)] = String(i)
    })

    // 被删掉的旧索引 = 初始索引集合 - 当前还存在的旧索引
    const initial = parseRows(column).map((r) => String(r.from))
    const nowSet = new Set(rows.map((r) => (r.from === null ? 'new' : String(r.from))))
    const removedOnly: string[] = []
    for (const idx of initial) if (!nowSet.has(idx)) removedOnly.push(idx)

    // 保留列上其它未知设置项，只覆盖我们负责的键
    const prevSettings = (column.settings as LabelSettings | null) ?? {}
    const nextSettings: LabelSettings = {
      ...prevSettings,
      color_mapping: colors,
      label_progress: progress,
      label_progress_mode: modes,
      done_colors: doneColors,
    }

    onSave(JSON.stringify({ labels }), JSON.stringify(nextSettings), { remap, removed: removedOnly })
  }

  return (
    <>
      <div className="labels-mask" onClick={onCancel} />
      <div className="labels-panel" role="dialog" aria-modal="true" aria-label="编辑标签">
        <header className="labels-panel__head">
          <span className="labels-panel__title">编辑标签</span>
          <span className="labels-panel__col">{column.title}</span>
        </header>

        <div
          className="labels-panel__body"
          onClick={() => {
            if (pickFor !== null) setPickFor(null)
          }}
        >
          {rows.length === 0 && <p className="labels-panel__empty">该列还没有标签</p>}

          {rows.map((r, i) => (
            <div key={i} className="labels-row2">
              <span className="labels-row2__idx">{i + 1}</span>
              <input
                className="labels-row2__text"
                value={r.text}
                disabled={busy}
                placeholder="标签名称"
                onChange={(e) => setText(i, e.target.value)}
              />
              <button
                type="button"
                className="labels-row2__del"
                disabled={busy}
                title="删除该标签。注意：会让后面的标签索引前移，已有值可能对应到别的标签上"
                onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
              >
                ×
              </button>

              {/* 第二行：颜色 / 完成度（是否完成 与 百分比 二选一） */}
              <div className="labels-row2__meta">
                <span className="labels-row2__colorwrap">
                  <button
                    type="button"
                    className="labels-row2__color"
                    style={{ background: r.color }}
                    aria-label="选择颜色"
                    title="选择颜色"
                    onClick={(e) => {
                      const r = e.currentTarget.getBoundingClientRect()
                      setPickPos({ x: Math.round(r.left), y: Math.round(r.bottom + 6) })
                      setPickFor(pickFor === i ? null : i)
                    }}
                  />
                  {/* 色板锚定在颜色按钮下方左侧（M9.2-fix3） */}
                  {pickFor === i && (
                    <div
                      className="labels-colors"
                      style={{ position: 'fixed', left: pickPos?.x ?? 0, top: pickPos?.y ?? 0 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {GROUP_COLOR_NAMES.map((n) => (
                        <button
                          key={n}
                          type="button"
                          className="color-swatch"
                          style={{ background: colorOf(n) }}
                          title={n}
                          aria-label={n}
                          onClick={() => setColor(i, colorOf(n))}
                        />
                      ))}
                    </div>
                  )}
                </span>
                <span className="labels-row2__label">完成度</span>
                <Select
                  className="labels-row2__done"
                  value={r.done}
                  disabled={busy}
                  title="选「完成」表示该标签即 100%；选「%」可填 0–100 的自定义进度"
                  onChange={(v) => setDone(i, v as DoneMode)}
                >
                  <option value="none">不设</option>
                  <option value="bool">完成</option>
                  <option value="percent">百分比</option>
                </Select>
                {r.done === 'percent' && (
                  <input
                    className="labels-row2__pct"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    value={r.pct}
                    disabled={busy}
                    aria-label="完成百分比"
                    onChange={(e) => setPct(i, e.target.value)}
                  />
                )}
              </div>

            </div>
          ))}

          <button
            type="button"
            className="labels-add"
            disabled={busy}
            onClick={() =>
              setRows((prev) => [
                ...prev,
                { from: null, text: '', color: '#c4c4c4', done: 'none', pct: 0 },
              ])
            }
          >
            + 添加标签
          </button>
        </div>

        <footer className="labels-panel__foot">
          <button type="button" className="labels-btn" disabled={busy} onClick={onCancel}>
            取消
          </button>
          <button type="button" className="labels-btn labels-btn--primary" disabled={busy} onClick={save}>
            保存
          </button>
        </footer>
      </div>
    </>
  )
}
